/**
 * Validation utilities
 * Verify config integrity before use
 */

import type { Pathway, PGD, Drug, PatientInput } from "../types";
import { pgdRegistry } from "../pgds";
import { pathwayRegistry } from "../pathways";
import { drugRegistry } from "../drugs";

/**
 * Validate that a pathway config is well-formed
 * Throws on error
 */
export function validatePathway(pathway: Pathway): void {
  // Entry node exists
  const entryNode = pathway.nodes.find((n) => n.id === pathway.entryNode);
  if (!entryNode) {
    throw new Error(`Pathway ${pathway.id}: entryNode "${pathway.entryNode}" not found`);
  }

  // All node IDs are unique
  const nodeIds = new Set<string>();
  for (const node of pathway.nodes) {
    if (nodeIds.has(node.id)) {
      throw new Error(`Pathway ${pathway.id}: duplicate node ID "${node.id}"`);
    }
    nodeIds.add(node.id);
  }

  // All outcome IDs are unique and defined
  const outcomeIds = Object.keys(pathway.outcomes);
  for (const outId of outcomeIds) {
    const outcome = pathway.outcomes[outId];
    if (!outcome) {
      throw new Error(`Pathway ${pathway.id}: outcome ${outId} not defined`);
    }
  }

  // All references (routing targets) point to valid nodes or outcomes
  for (const node of pathway.nodes) {
    validateNodeRouting(node, nodeIds, new Set(outcomeIds), pathway.id);
  }

  // All referenced PGDs exist
  for (const pgdRef of pathway.pgdRefs) {
    if (!pgdRegistry[`pgd-${pgdRef.toLowerCase()}`] && !pgdRegistry[pgdRef]) {
      throw new Error(`Pathway ${pathway.id}: PGD reference "${pgdRef}" not found in registry`);
    }
  }

  // Drug selection rule.pgdId references real PGDs
  for (const node of pathway.nodes as any[]) {
    if (node.type === "drug_selection") {
      for (const r of node.rules || []) {
        if (r.pgdId && !pgdRegistry[r.pgdId]) {
          throw new Error(
            `Pathway ${pathway.id} node ${node.id}: drug rule references unknown PGD "${r.pgdId}"`,
          );
        }
      }
    }
  }

  // outcomeOverrides reference real outcomes
  for (const ov of pathway.outcomeOverrides || []) {
    if (!pathway.outcomes[ov.outcomeId]) {
      throw new Error(
        `Pathway ${pathway.id}: outcomeOverride references unknown outcome "${ov.outcomeId}"`,
      );
    }
  }
}

/**
 * Validate that all routing targets in a node exist
 */
function validateNodeRouting(
  node: any,
  nodeIds: Set<string>,
  outcomeIds: Set<string>,
  pathwayId: string
): void {
  const targets: string[] = [];

  if (node.type === "yes_no") {
    targets.push(getRefString(node.yes));
    targets.push(getRefString(node.no));
  } else if (node.type === "checklist") {
    targets.push(getRefString(node.anySelected));
    targets.push(getRefString(node.noneSelected));
  } else if (node.type === "single_select") {
    for (const key of Object.keys(node.routing || {})) {
      targets.push(getRefString(node.routing[key]));
    }
  } else if (node.type === "severity") {
    targets.push(getRefString(node.mild));
    targets.push(getRefString(node.moderate));
    targets.push(getRefString(node.severe));
  } else if (node.type === "gateway_progression") {
    targets.push(node.next);
  } else if (node.type === "drug_selection") {
    targets.push(node.next);
  } else if (node.type === "pgd_exclusion_check") {
    targets.push(getRefString(node.anyTriggered));
    targets.push(getRefString(node.allCleared));
    if (node.pgdIdOverride && !pgdRegistry[node.pgdIdOverride]) {
      throw new Error(
        `Pathway ${pathwayId} node ${node.id}: pgdIdOverride references unknown PGD "${node.pgdIdOverride}"`,
      );
    }
  } else if (node.type === "flag") {
    targets.push(node.next);
  } else if (node.type === "weight_capture") {
    targets.push(node.next);
  } else if (node.type === "bmi_capture") {
    targets.push(getRefString((node as any).next));
  } else if (node.type === "condition_route") {
    for (const condition of node.conditions || []) {
      targets.push(getRefString(condition.then));
    }
    targets.push(getRefString(node.default));
  } else if (node.type === "multi_select") {
    if (node.thresholdMet) targets.push(getRefString(node.thresholdMet));
    if (node.thresholdNotMet) targets.push(getRefString(node.thresholdNotMet));
  } else if (node.type === "score") {
    // Function-based routing cannot be validated statically — skip
    if (node.routing && typeof node.routing !== "function") {
      for (const val of Object.values(node.routing as Record<string, any>)) {
        if (typeof val === "string" || (val && typeof val === "object" && "outcomeId" in val)) {
          targets.push(getRefString(val));
        }
      }
    }
  }

  for (const target of targets) {
    if (target && !nodeIds.has(target) && !outcomeIds.has(target)) {
      throw new Error(
        `Pathway ${pathwayId} node ${node.id}: routing target "${target}" not found`
      );
    }
  }
}

/**
 * Extract string reference from either string or OutcomeRef
 */
function getRefString(ref: string | { outcomeId: string }): string {
  if (typeof ref === "string") return ref;
  return ref.outcomeId;
}

/**
 * Validate that a PGD references valid drugs
 */
export function validatePgd(pgd: PGD): void {
  if (!drugRegistry[pgd.drugRef]) {
    throw new Error(`PGD ${pgd.id}: drug reference "${pgd.drugRef}" not found in registry`);
  }

  const drug = drugRegistry[pgd.drugRef];

  // All dose band formulations exist
  for (const band of pgd.doseBands) {
    for (const formChoice of band.formulations) {
      const formulation = drug.formulations.find((f) => f.id === formChoice.formulationId);
      if (!formulation) {
        throw new Error(
          `PGD ${pgd.id}: formulation "${formChoice.formulationId}" not found in drug "${pgd.drugRef}"`
        );
      }
    }
  }

  // Dates are valid — new Date() never throws in JS, so check .getTime() instead
  if (isNaN(new Date(pgd.validFrom).getTime())) {
    throw new Error(`PGD ${pgd.id}: invalid validFrom date "${pgd.validFrom}"`);
  }
  if (isNaN(new Date(pgd.expiryDate).getTime())) {
    throw new Error(`PGD ${pgd.id}: invalid expiryDate date "${pgd.expiryDate}"`);
  }
}

/**
 * Validate a patient input object
 */
export function validatePatientInput(patient: PatientInput): void {
  if (!patient.patientId) {
    throw new Error("Patient input missing patientId");
  }
  if (patient.ageYears !== undefined && patient.ageYears !== null) {
    if (patient.ageYears < 0 || patient.ageYears > 150) {
      throw new Error(`Invalid age: ${patient.ageYears}`);
    }
  }
  if (isNaN(new Date(patient.dateOfBirth).getTime())) {
    throw new Error(`Invalid dateOfBirth: ${patient.dateOfBirth}`);
  }
}

/**
 * Run all validations on startup
 */
export function validateAllConfigs(): void {
  // Validate all pathways
  for (const pathway of Object.values(pathwayRegistry)) {
    validatePathway(pathway);
  }

  // Validate all PGDs
  for (const pgd of Object.values(pgdRegistry)) {
    validatePgd(pgd);
  }
}
